import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, Compass, ShieldCheck, MapPin } from "lucide-react";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { Button } from "@/components/ui/button";
import LazyLocationMap from "@/components/LazyLocationMap";
import { locations } from "@/lib/locations-data";

const mainLocation = locations.find((l) => l.isPrimary) ?? locations[0];
const otherLocations = locations.filter((l) => !l.isPrimary);

const ServiceAreas = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="locations" className="relative bg-background overflow-hidden">
      {/* Full-width map canvas */}
      <div className="relative w-full">
        <LazyLocationMap />

        {/* Floating glass content panel */}
        <div className="container-max relative z-10 -mt-24 md:-mt-32 lg:-mt-40 pb-16 md:pb-20 px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative mx-auto max-w-5xl rounded-[28px] border border-border/70 bg-card/95 backdrop-blur-xl p-8 md:p-10 lg:p-12"
            style={{ boxShadow: "0 40px 80px -30px hsl(0 0% 5% / 0.4), 0 8px 24px -12px hsl(var(--primary) / 0.2)" }}
          >
            <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/25 px-3.5 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-primary mb-4">
                  <Compass className="h-3 w-3" /> Coverage
                </span>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold">
                  Areas We <span className="text-gradient-primary">Serve</span>
                </h2>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
                  Serving homeowners and businesses within a 30-mile radius — local crews, fast scheduling, and free, no-pressure estimates.
                </p>

                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-2 rounded-xl bg-primary/10 border border-primary/20 px-3 py-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    <span className="text-sm font-bold text-foreground">30-Mile Radius</span>
                  </div>
                  <div className="flex items-center gap-2 rounded-xl bg-secondary/10 border border-secondary/20 px-3 py-2">
                    <MapPin className="h-4 w-4 text-secondary" />
                    <span className="text-sm font-bold text-foreground">{locations.length} Locations</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button size="lg" onClick={openQuoteForm} className="gap-2 px-6 rounded-xl shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.55)]">
                    Get Free Estimate <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button asChild variant="outline" size="lg" className="gap-2 px-6 rounded-xl">
                    <a href="tel:+15405604862">
                      <Phone className="h-4 w-4 text-primary" /> (540) 560-4862
                    </a>
                  </Button>
                </div>
              </div>

              {/* Cities grid */}
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Areas We Serve
                  </span>
                  <Link to="/locations" className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1">
                    View all<span className="sr-only"> service areas</span> <ArrowRight className="h-3 w-3" aria-hidden="true" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to={`/locations/${mainLocation.slug}`}
                    className="col-span-2 group flex items-center gap-3 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/25 px-4 py-3 hover:border-primary/50 hover:from-primary/15 transition-colors"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground shrink-0">
                      <MapPin className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-sm text-foreground truncate">{mainLocation.name}</p>
                      <p className="text-[11px] text-muted-foreground">Home base · {mainLocation.state}</p>
                    </div>
                  </Link>
                  {otherLocations.map((loc) => (
                    <Link
                      key={loc.slug}
                      to={`/locations/${loc.slug}`}
                      className="group flex items-center gap-2 rounded-lg bg-background border border-border/70 px-3 py-2.5 hover:border-primary/40 hover:bg-primary/5 transition-colors"
                    >
                      <span className="h-2 w-2 rounded-full bg-secondary shrink-0" />
                      <span className="text-sm font-semibold text-foreground truncate">{loc.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreas;
